import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import isEqual from 'lodash.isequal';
import {
  IconLoader,
  Select,
  Table,
  BaseellaStatusTitle,
  Currency,
  formatBalance,
  BaseellaStatus,
  useQueryState,
  useFilters,
  SchemaFilterType,
  useGetList,
  Filters,
  usePrevious,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { useLazyGetReportsCurrenciesQuery } from '../../store/dictionaries/service';
import {
  columns,
  columnsLabels,
  filtersSchema,
  schema,
} from './variables/list';
import {
  useLazyGetTransactionStatusReportQuery,
  useLazyGetReportsTransactionsTypesQuery,
} from './store/service';
import { TransactionStatusReport as TransactionStatusReportProps } from './types';
import { clearReport } from './store/slice';
import calculateTotal from './helpers/calculate-total';

import cn from './styles.module.css';

const formatTransactionStatusReport = (
  list: TransactionStatusReportProps[],
  currenciesList?: Currency[],
): TransactionStatusReportProps[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, React.JSX.Element> = {
      baseellaStatus: (
        <div
          className={`${cn.totalRow} ${
            result.baseellaStatus === 'TOTAL' ? cn.totalValue : ''
          }`}
        >
          {result.baseellaStatus !== 'TOTAL'
            ? BaseellaStatusTitle[
                result.baseellaStatus as keyof typeof BaseellaStatus
              ]
            : 'Total'}
        </div>
      ),
    };

    item.totals?.forEach((value) => {
      render[value.currencyId] = (
        <div className={result.baseellaStatus === 'TOTAL' ? cn.totalValue : ''}>
          {formatBalance(
            value.sum,
            currenciesList?.find(
              (currency) => currency.id === value.currencyId,
            ),
          )}
        </div>
      );
    });

    result.render = render;

    return result;
  });

  return nextList;
};

const defaultStatuses = [
  BaseellaStatus.DRAFT,
  BaseellaStatus.NONE,
  BaseellaStatus.NEW,
  BaseellaStatus.PROC,
  BaseellaStatus.SENT,
  BaseellaStatus.ARCH,
  BaseellaStatus.REV,
  BaseellaStatus.AML,
  BaseellaStatus.AML_INFO,
  BaseellaStatus.AML_REJ,
  BaseellaStatus.AML_DONE,
  BaseellaStatus.REJECTED,
  BaseellaStatus.NOT_SIGNED,
  BaseellaStatus.READY_TO_SEND,
  BaseellaStatus.READY_TO_PSP,
  BaseellaStatus.ACCP_OUT,
  BaseellaStatus.UNDEFINED_FUNDS,
];

const TransactionStatusReport = () => {
  const [formattedColumns, setFormattedColumns] = useState<
    { name: string; label: string; hidden: boolean; sortable?: boolean }[]
  >(
    columns as {
      name: string;
      label: string;
      hidden: boolean;
      sortable?: boolean;
    }[],
  );
  const [selectedType, setSelectedType] = useState<string>('');
  const [defaultFilters, setDefaultFilters] = useState<Filters>();
  const [filteredList, setFilteredList] =
    useState<TransactionStatusReportProps[]>();

  const [getReportsCurrencies, { isLoading: currenciesLoading }] =
    useLazyGetReportsCurrenciesQuery();
  const [getTransactionTypes, { isLoading: transactionTypesLoading }] =
    useLazyGetReportsTransactionsTypesQuery();
  const [getTransactionsStatusReport, { isLoading, isFetching }] =
    useLazyGetTransactionStatusReportQuery();

  const {
    reportsDictionaries: { currencies, currenciesList },
    transactionsStatus: { transactionTypes, transactionsStatus },
  } = useAppSelector((state) => state);

  const prevSelectedType = usePrevious(selectedType);
  const prevTransactionStatus = usePrevious(transactionsStatus);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { search } = useLocation();
  const { filters, onFilterChange } = useQueryState(schema);

  const formattedData = useMemo(() => {
    const nextTransactionStatusReport = filteredList?.map((item) => {
      const result = { ...item };

      currencies?.forEach((currency) => {
        result[currency.value] = '-';
      });

      result.id = item.baseellaStatus;
      result.total = '-';

      return result;
    });

    const data = formatTransactionStatusReport(
      nextTransactionStatusReport as TransactionStatusReportProps[],
      currenciesList,
    );

    return data;
  }, [filteredList, currenciesList, currencies]);

  const onFilterValue = (values: Filters) => {
    onFilterChange(values);

    const nextList: TransactionStatusReportProps[] = [];

    transactionsStatus?.forEach((item) => {
      if (
        values.baseellaStatus?.value?.find(
          (status) => item.baseellaStatus === status,
        )
      ) {
        nextList.push(item);
      }

      setFilteredList(calculateTotal(nextList));
    });
  };

  const { onFilter, errorList } = useGetList({
    getList: getTransactionsStatusReport,
    onFilterChange,
    onSortChange: () => {},
    itemId: selectedType,
    schema,
  });

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: () => {},
  });

  useEffect(() => {
    if (!currencies) {
      getReportsCurrencies();
    }

    if (!transactionTypes) {
      getTransactionTypes();
    }
  }, [currencies, transactionTypes]);

  useEffect(() => {
    if (!selectedType && transactionsStatus) {
      dispatch(clearReport());
    }
  }, [selectedType, transactionsStatus]);

  useEffect(() => {
    if (
      transactionsStatus &&
      (!filteredList || !isEqual(transactionsStatus, prevTransactionStatus))
    ) {
      onFilterValue(filters);
    }
  }, [transactionsStatus, filteredList, prevTransactionStatus, filters]);

  useEffect(() => {
    if (selectedType && prevSelectedType !== selectedType) {
      const nextFilters: Filters = {
        transactionType: {
          value: [selectedType],
          type: SchemaFilterType.STRING,
        },
      };

      if (!filters.baseellaStatus) {
        nextFilters.baseellaStatus = {
          value: defaultStatuses,
          type: SchemaFilterType.STRING,
        };
      } else {
        nextFilters.baseellaStatus = {
          value: filters?.baseellaStatus?.value,
          type: SchemaFilterType.STRING,
        };
      }

      onFilter({
        ...nextFilters,
      });
    }
  }, [selectedType, prevSelectedType, filters]);

  useEffect(() => {
    let nextColumns = (
      [] as {
        name: string;
        label: string;
        hidden: boolean;
        sortable?: boolean;
      }[]
    ).concat(
      columns as {
        name: string;
        label: string;
        hidden: boolean;
        sortable?: boolean;
      }[],
    );

    if (currencies) {
      currencies?.forEach((item) => {
        nextColumns.push({
          name: item.value as string,
          label: item.name,
          hidden: false,
          sortable: false,
        });
      });
    } else {
      nextColumns = columns as {
        name: string;
        label: string;
        hidden: boolean;
        sortable?: boolean;
      }[];
    }

    setFormattedColumns(nextColumns);
  }, [currencies]);

  useEffect(() => {
    if (
      selectedType &&
      !transactionsStatus &&
      !isLoading &&
      defaultFilters &&
      Object.keys(defaultFilters)?.length
    ) {
      const nextFilters: Filters = {
        transactionType: {
          value: [selectedType],
          type: SchemaFilterType.STRING,
        },
      };

      if (!defaultFilters.baseellaStatus) {
        nextFilters.baseellaStatus = {
          value: defaultStatuses,
          type: SchemaFilterType.STRING,
        };
      } else {
        nextFilters.baseellaStatus = {
          value: defaultFilters?.baseellaStatus?.value,
          type: SchemaFilterType.STRING,
        };
      }

      onFilter({
        ...nextFilters,
      });
    }
  }, [selectedType, filters, transactionsStatus, isLoading, defaultFilters]);

  useEffect(() => {
    if (search) {
      const parsed = queryString.parse(search);

      if (parsed.transactionType && !selectedType) {
        const nextFilters: Filters = {
          transactionType: {
            value: [parsed.transactionType as string],
            type: SchemaFilterType.STRING,
          },
        };

        setSelectedType(parsed.transactionType as string);

        if (parsed.baseellaStatus) {
          nextFilters.baseellaStatus = {
            value: parsed.baseellaStatus as string[],
            type: SchemaFilterType.STRING,
          };
        }

        setDefaultFilters(nextFilters);
        onFilterChange(nextFilters);
      }
    } else if (!search && !selectedType) {
      setDefaultFilters({
        baseellaStatus: {
          value: defaultStatuses,
          type: SchemaFilterType.STRING,
        },
      });
    }
  }, [search, selectedType]);

  useEffect(() => {
    let title = 'Transaction status report';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content = <div />;

  if (currenciesLoading || transactionTypesLoading) {
    content = (
      <div className={cn.transactionStatusReportLoader}>
        <IconLoader />
      </div>
    );
  } else if (
    !currenciesLoading &&
    !transactionTypesLoading &&
    currencies?.length
  ) {
    content = (
      <div className={cn.transactionsStatusTableWrapper}>
        <Table
          tableFormClassName={cn.transcationStatusReportTable}
          tableBodyClassName={`${!selectedType ? cn.emptyTable : ''} ${
            isLoading || isFetching ? cn.tableLoading : ''
          }`}
          columns={formattedColumns}
          data={formattedData || []}
          filtersComponent={filtersComponent}
          sort={{}}
          onSort={() => {}}
          filters={filters}
          columnsLabels={columnsLabels}
          error={errorList}
          onFilterChange={(values) => {
            const nextFilters = { ...values };

            if (!values.baseellaStatus) {
              nextFilters.baseellaStatus = {
                value: defaultStatuses,
                type: SchemaFilterType.STRING,
              };
            }

            onFilterValue(nextFilters);
          }}
          handleSubmit={handleSubmit}
          isLoading={isLoading || isFetching}
          tableTopClassName={cn.statusReportTableTop}
          hiddenFiltersTags={
            filters?.baseellaStatus?.value?.length === defaultStatuses?.length
              ? ['baseellaStatus']
              : []
          }
          withScroll
          withVerticalScroll
        />
        {!selectedType && (
          <div className={cn.transactionStatusMessage}>
            Select Transaction Type
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn.transactionStatusReport}>
      <div className={cn.transactionStatusReportTop}>
        <div className={cn.transactionStatusReportTitle}>
          Transaction Status Report
        </div>
        <div className={cn.transactionStatusReportControls}>
          <Select
            name="transactionTypes"
            value={selectedType}
            list={transactionTypes
              ?.filter(
                (item) =>
                  item.name !== 'INTERNAL_PAYMENT' &&
                  item.name !== 'CURRENCY_EXCHANGE' &&
                  item.name !== 'FEE' &&
                  item.name !== 'GENERAL_LEDGER',
              )
              ?.map((item) => ({
                name: item.name
                  ?.split('_')
                  ?.map(
                    (value) =>
                      `${value.charAt(0)}${value.slice(1).toLowerCase()}`,
                  )
                  ?.join(' '),
                value: item.id,
              }))}
            onChange={(e) => {
              setSelectedType(e.target.value as string);
              navigate(`?transactionType=${e.target.value}`);
            }}
            placeholder="Select Transaction type"
            className={cn.typesSelect}
            disabled={!transactionTypes || transactionTypesLoading}
          />
        </div>
      </div>

      {content}
    </div>
  );
};

export default TransactionStatusReport;
